// react
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

// tanstack
import { createFileRoute, useNavigate } from '@tanstack/react-router';

// material ui
import { Container } from '@mui/material';

// api
import {
  useBuildingsCreateAsyncBybuildingDtoMutation,
  useBuildingsCreateBillingMeterAsyncBybuildingIdbillingMeterDtoMutation,
  useBuildingsDeleteBuildingBillingMeterAsyncBybuildingIdbillingMeterIdMutation,
  useBuildingsGetCollectionAsyncByqueryOptionsQuery,
  useBuildingsUpdateAsyncBybuildingIdbuildingDtoMutation,
} from '@/api/generated/recalc/endpoints/v1/buildings';
import type {
  CreateBillingMeterDto,
  CreateBuildingDto,
  EditBuildingDto,
  ReadBillingMeterDto,
} from '@/api/generated/recalc/endpoints/v1/buildings';
import { useUnitsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/units';
import { useGetAccountsQuery } from '@/api/recalc/endpoints/accountsApiSlice';

// shared components
import PageHeader from '@/components/layout/PageHeader';

// feature-components
import BuildingDataGrid from '@/features/building-optimization/components/building/BuildingDataGrid';
import CreateBuildingDialog from '@/features/building-optimization/components/building/CreateBuildingDialog';
import EditBuildingBillingMeterDialog from '@/features/building-optimization/components/building/EditBuildingBillingMeterDialog';
import EditBuildingDialog from '@/features/building-optimization/components/building/EditBuildingDialog';

// components
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import ErrorBoundary from '@/routes/-ErrorBoundary';

type BuildingRow = {
  id: number;
  Name?: string;
  AccountId: number;
  billingMeters?: ReadBillingMeterDto[];
  state?: {
    BillingMeterHealthCheck?: unknown;
    [key: string]: unknown;
  };
  baselineDayInterval?: number;
  baselineDayOffset?: string;
  allowSaturdayBaselineDay?: boolean;
  address?: string;
  latitude?: number | null;
  longitude?: number | null;
  userInput?: {
    [key: string]: unknown;
  };
};

export const Route = createFileRoute(
  '/$accountId/_admin/operations/predictive-heat-control/buildings/',
)({
  component: RouteComponent,
  errorComponent: ErrorBoundary,
  staticData: { crumb: 'glossary:buildings' },
});

function RouteComponent() {
  const account = useSelector(getSelectedAccount);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [openCreateDialog, setOpenCreateDialog] = React.useState(false);
  const [openEditDialog, setOpenEditDialog] = React.useState(false);
  const [buildingBeingEdited, setBuildingBeingEdited] =
    React.useState<BuildingRow | null>(null);

  const [
    openEditBuildingBillingMetersDialog,
    setOpenEditBuildingBillingMetersDialog,
  ] = React.useState(false);

  const { data: buildings } = useBuildingsGetCollectionAsyncByqueryOptionsQuery(
    {
      orderby: 'BuildingId',
      'Account.AccountId': `eq(${account.AccountId})`,
      top: 10000,
    },
    {
      skip: !account.AccountId || account.AccountId === 1,
    },
  );

  const { data: allBuildings } =
    useBuildingsGetCollectionAsyncByqueryOptionsQuery(
      {
        orderby: 'BuildingId',
        top: 10000,
      },
      {
        skip: account.AccountId !== 1,
      },
    );

  const { data: units } = useUnitsGetCollectionAsyncByqueryOptionsQuery(
    { 'Account.AccountId': `eq(${buildingBeingEdited?.AccountId})` },
    {
      skip:
        !buildingBeingEdited?.AccountId || !openEditBuildingBillingMetersDialog,
    },
  );

  const { data: accounts } = useGetAccountsQuery();

  const [postBuilding] = useBuildingsCreateAsyncBybuildingDtoMutation();
  const [postBillingMeter] =
    useBuildingsCreateBillingMeterAsyncBybuildingIdbillingMeterDtoMutation();
  const [deleteBillingMeter] =
    useBuildingsDeleteBuildingBillingMeterAsyncBybuildingIdbillingMeterIdMutation();
  const [updateBuilding] =
    useBuildingsUpdateAsyncBybuildingIdbuildingDtoMutation();

  const onViewBuilding = (building: BuildingRow) => {
    navigate({
      to: `/$accountId/operations/predictive-heat-control/buildings/${encodeURIComponent(building.Name)}`,
      state: {
        building,
        accounts,
      } as never,
    });
  };

  const handleEditBuilding = async (building: EditBuildingDto) => {
    if (!buildingBeingEdited?.id) {
      return;
    }

    const result = await updateBuilding({
      buildingId: buildingBeingEdited.id,
      editBuildingDto: building,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: 'Building was updated',
          severity: 'success',
          response: result.data,
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const handleCreateBuilding = async (
    building: Omit<CreateBuildingDto, 'AccountId'>,
  ) => {
    const payload: CreateBuildingDto = {
      ...building,
      AccountId: account.AccountId,
    };

    const result = await postBuilding({
      createBuildingDto: payload,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: 'Building was created',
          severity: 'success',
          response: result.data,
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const handleAddBillingMeter = async (billingMeter: CreateBillingMeterDto) => {
    if (!buildingBeingEdited?.id) {
      return;
    }

    const addResult = await postBillingMeter({
      buildingId: buildingBeingEdited.id,
      createBillingMeterDto: billingMeter,
    });

    if (!('error' in addResult)) {
      dispatch(
        showSnackbar({
          message: 'Billing meter was added',
          severity: 'success',
          response: addResult.data,
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const handleDeleteBillingMeter = async (billingMeterId: number) => {
    if (!buildingBeingEdited?.id) {
      return;
    }

    const deleteResult = await deleteBillingMeter({
      buildingId: buildingBeingEdited.id,
      billingMeterId,
    });

    if (!('error' in deleteResult)) {
      dispatch(
        showSnackbar({
          message: 'Billing meter was removed',
          severity: 'success',
          response: deleteResult.data,
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const onEditBuilding = (building: BuildingRow) => {
    setBuildingBeingEdited(building);
    setOpenEditDialog(true);
  };

  const onEditBillingMeters = (building: BuildingRow) => {
    setBuildingBeingEdited(building);
    setOpenEditBuildingBillingMetersDialog(true);
  };

  if (!account.AccountId) return;

  return (
    <Container maxWidth={false} sx={{ py: 2 }}>
      <PageHeader title="Bygninger" disableArrow />
      <BuildingDataGrid
        buildings={account.AccountId === 1 ? allBuildings : buildings}
        accounts={accounts}
        onCreateBuilding={() => setOpenCreateDialog(true)}
        onEditBuilding={onEditBuilding}
        onEditBillingMeters={onEditBillingMeters}
        onViewBuilding={onViewBuilding}
      />

      <CreateBuildingDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        account={account}
        handleCreateBuilding={handleCreateBuilding}
      />

      {buildingBeingEdited && (
        <EditBuildingDialog
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
          building={buildingBeingEdited}
          handleEditBuilding={handleEditBuilding}
        />
      )}

      <EditBuildingBillingMeterDialog
        open={openEditBuildingBillingMetersDialog}
        onClose={() => setOpenEditBuildingBillingMetersDialog(false)}
        units={units}
        billingMeters={buildingBeingEdited?.billingMeters || []}
        billingMeterHealthCheck={
          buildingBeingEdited?.state?.BillingMeterHealthCheck
        }
        handleAddBillingMeter={handleAddBillingMeter}
        handleDeleteBillingMeter={handleDeleteBillingMeter}
      />
    </Container>
  );
}
